import React, { Component } from "react";
import { Link } from "gatsby";
import GlobalLayout from "../../components/layouts/layout-global";
import SEO from "../../components/seo";
import SectionPadding from "../../components/sections/section-padding";
import SectionDefault from "../../components/sections/section-default";
import TextBoxGray from "../../components/text-box-gray";

export const Head = ({ location }) => (
  <SEO
    title="Checklist for authors"
    // prettier-ignore
    keywords={[
            "NACC", "checklist", "credit", "acknowledgment", "statement", "PI", "citation", "grant", "researcher", "author", "publish", "publication",
            "agreement", "confidentiality", "registry", "NACC", "Alzheimer&apos;s", "ADRC", "dementia", "MCI", "degeneration", "research", "data", "NIA",
            "NIH", "database", "neuropathology", "LBD", "FTLD", "neurology", "gerontology", "age", "cognitive", "genetics", "GWAS", "APOE"
          ]}
    description="Checklist for authors using NACC data."
    pathname={location.pathname}
  />
);

class AuthorChecklist extends Component {
  render() {
    return (
      <GlobalLayout location={this.props.location}>
        {/* intro */}
        <SectionDefault heading="Checklist for authors">
          <SectionPadding>
            <p className="uk-text-lead">
              After receiving NACC data through the{" "}
              <Link to="/requesting-data/data-request-process">data request process</Link>, use the resources below to
              prepare for publication and review.
            </p>
            <ol className="uk-list uk-list-bullet">
              <li>
                Be sure to include{" "}
                <a data-uk-scroll="offset: 20" href="/publish-project/authors-checklist#citing%20the%20grant">
                  the NACC/NIA grant citation.
                </a>
              </li>
              <li>
                You are required to <Link to="/publish-project/submit-manuscript">submit your publication</Link> to NACC
                for review before releasing manuscripts, meeting abstracts, or any other research results.
              </li>
              <li>
                In order to comply with the{" "}
                <a
                  href="http://publicaccess.nih.gov"
                  title="NIH public access policy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  NIH public access policy
                </a>
                , all authors using NACC data must{" "}
                <Link to="/publish-project/pmc-resources">submit their work to PubMed Central (PMC)</Link>.
              </li>
            </ol>
          </SectionPadding>
        </SectionDefault>

        <SectionDefault subheading="ADC vs. ADRC">
          <p>
            Beginning in summer 2019, the NIA Alzheimer&apos;s Disease Research Center (ADRC) program is referring to
            all centers as Alzheimer&apos;s Disease Research Centers (ADRCs).
          </p>
          <p>
            The NACC database contains data from both Alzheimer&apos;s Disease Core Centers (ADCCs) and ADRCs. If
            referring to the NACC database, you may refer to the centers as ADCs, but if referring to the current NIA
            program, we recommend referring to the centers as ADRCs.
          </p>
        </SectionDefault>

        <SectionDefault subheading="Citing the grant">
          <p className="uk-margin-remove-top">
            In abstracts and posters, acknowledge the NACC grant number, and the ADRCs as the source of the data.{" "}
            <strong>PLEASE NOTE:</strong> MRI and PET data from the Standardized Centralized Alzheimer&apos;s Disease
            Neuroimaging (SCAN) Initiative, and data from the Alzheimer’s Disease Genetic Consortium (ADGC) have
            additional grant information and ADRC information as listed below.
          </p>
          <strong className="uk-margin-small-bottom">
            If using NACC data, the following statement acknowledging the NACC grant and Center support is required in
            presentations and the acknowledgments section of manuscripts:
          </strong>
          <TextBoxGray>
            <p className="uk-text-small">
              <i>
                The NACC database is funded by NIA/NIH Grant U24 AG072122. NACC data are contributed by the NIA-funded
                ADRCs: P30 AG062429 (PI James Brewer, MD, PhD), P30 AG066468 (PI Oscar Lopez, MD), P30 AG062421 (PI
                Bradley Hyman, MD, PhD), P30 AG066509 (PI Thomas Grabowski, MD), P30 AG066514 (PI Mary Sano, PhD), P30
                AG066530 (PI Helena Chui, MD), P30 AG066507 (PI Marilyn Albert, PhD), P30 AG066444 (PI David Holtzman,
                MD), P30 AG066518 (PI Lisa Silbert, MD, MCR), P30 AG066512 (PI Thomas Wisniewski, MD), P30 AG066462 (PI
                Scott Small, MD), P30 AG072979 (PI David Wolk, MD), P30 AG072972 (PI Charles DeCarli, MD), P30 AG072976
                (PI Andrew Saykin, PsyD), P30 AG072975 (PI Julie A. Schneider, MD, MS), P30 AG072978 (PI Ann McKee, MD),
                P30 AG072977 (PI Robert Vassar, PhD), P30 AG066519 (PI Frank LaFerla, PhD), P30 AG062677 (PI Ronald
                Petersen, MD, PhD), P30 AG079280 (PI Jessica Langbaum, PhD), P30 AG062422 (PI Gil Rabinovici, MD), P30
                AG066511 (PI Allan Levey, MD, PhD), P30 AG072946 (PI Linda Van Eldik, PhD), P30 AG062715 (PI Sanjay
                Asthana, MD, FRCP), P30 AG072973 (PI Russell Swerdlow, MD), P30 AG066506 (PI Glenn Smith, PhD, ABPP),
                P30 AG066508 (PI Stephen Strittmatter, MD, PhD), P30 AG066515 (PI Victor Henderson, MD, MS), P30
                AG072947 (PI Suzanne Craft, PhD), P30 AG072931 (PI Henry Paulson, MD, PhD), P30 AG066546 (PI Sudha
                Seshadri, MD), P30 AG086401 (PI Erik Roberson, MD, PhD), P30 AG086404 (PI Gary Rosenberg, MD), P20
                AG068082 (PI Angela Jefferson, PhD), P30 AG072958 (PI Heather Whitson, MD), P30 AG072959 (PI James
                Leverenz, MD).
              </i>
            </p>
          </TextBoxGray>
          <p>
            Please do <strong>not</strong> state that “This study was supported by U24 AG072122.” (This is acceptable
            ONLY if NACC supported your study directly by awarding you funds through a{" "}
            <Link to="/nacc-productivity/new-investigator-awards">NACC New Investigator grant</Link>
            .)
          </p>

          <strong className="uk-margin-small-bottom">
            If using MRI and/or PET data from the Standardized Centralized Alzheimer&apos;s Disease Neuroimaging (SCAN)
            Initiative, the following statement acknowledging the SCAN grant and Centers is required in presentations
            and the acknowledgments section of manuscripts:
          </strong>
          <TextBoxGray>
            <p className="uk-text-small">
              The NACC database is funded by NIA/NIH Grant U24 AG072122. SCAN is a multi-institutional project that was
              funded as a U24 grant (AG067418) by the National Institute on Aging in May 2020. Data collected by SCAN
              and shared by NACC are contributed by the NIA-funded ADRCs as follows:
            </p>
            <p className="uk-text-small">
              <i>
                Arizona Alzheimer’s Center - P30 AG072980 (PI: Eric Reiman, MD); R01 AG069453 (PI: Eric Reiman
                (contact), MD); P30 AG019610 (PI: Eric Reiman, MD); and the State of Arizona which provided additional
                funding supporting our center; Boston University - P30 AG013846 (PI Neil Kowall MD); Cleveland ADRC -
                P30 AG062428 (James Leverenz, MD); Cleveland Clinic, Las Vegas – P20AG068053; Columbia - P50 AG008702
                (PI Scott Small MD); Duke/UNC ADRC – P30 AG072958; Emory University - P30AG066511 (PI Levey Allan, MD,
                PhD); Indiana University - R01 AG19771 (PI Andrew Saykin, PsyD); P30 AG10133 (PI Andrew Saykin, PsyD);
                P30 AG072976 (PI Andrew Saykin, PsyD); R01 AG061788 (PI Shannon Risacher, PhD); R01 AG053993 (PI
                Yu-Chien Wu, MD, PhD); U01 AG057195 (PI Liana Apostolova, MD); U19 AG063911 (PI Bradley Boeve, MD); and
                the Indiana University Department of Radiology and Imaging Sciences; Johns Hopkins - P30 AG066507 (PI
                Marilyn Albert, Phd.); Mayo Clinic - P50 AG016574 (PI Ronald Petersen MD PhD); Mount Sinai - P30
                AG066514 (PI Mary Sano, PhD); R01 AG054110 (PI Trey Hedden, PhD); R01 AG053509 (PI Trey Hedden, PhD);
                New York University - P30AG066512-01S2 (PI Thomas Wisniewski, MD); R01AG056031 (PI Ricardo Osorio, MD);
                R01AG056531 (PIs Ricardo Osorio, MD; Girardin Jean-Louis, PhD); Northwestern University - P30 AG013854
                (PI Robert Vassar PhD); R01 AG045571 (PI Emily Rogalski, PhD); R56 AG045571, (PI Emily Rogalski, PhD);
                R01 AG067781, (PI Emily Rogalski, PhD); U19 AG073153, (PI Emily Rogalski, PhD); R01 DC008552, (M.-Marsel
                Mesulam, MD); R01 AG077444, (PIs M.-Marsel Mesulam, MD, Emily Rogalski, PhD); R01 NS075075 (PI Emily
                Rogalski, PhD); R01 AG056258 (PI Emily Rogalski, PhD); Oregon Health and Science University - P30
                AG008017 (PI Jeffrey Kaye MD); R56 AG074321 (PI Jeffrey Kaye, MD); Rush University - P30 AG010161 (PI
                David Bennett MD); Stanford – P30AG066515; P50 AG047366 (PI Victor Henderson MD MS); University of
                Alabama, Birmingham – P20; University of California, Davis - P30 AG10129 (PI Charles DeCarli, MD); P30
                AG072972 (PI Charles DeCarli, MD); University of California, Irvine - P50 AG016573 (PI Frank LaFerla
                PhD); University of California, San Diego - P30AG062429 (PI James Brewer, MD, PhD); University of
                California, San Francisco - P30 AG062422 (Rabinovici, Gil D., MD); University of Kansas - P30 AG035982
                (Russell Swerdlow, MD); University of Kentucky - P30 AG028283-15S1 (PIs Linda Van Eldik, PhD and Brian
                Gold, PhD); University of Michigan ADRC - P30AG053760 (PI Henry Paulson, MD, PhD) P30AG072931 (PI Henry
                Paulson, MD, PhD) Cure Alzheimer&apos;s Fund 200775 - (PI Henry Paulson, MD, PhD) U19 NS120384 (PI
                Charles DeCarli, MD, University of Michigan Site PI Henry Paulson, MD, PhD) R01 AG068338 (MPI Bruno
                Giordani, PhD, Carol Persad, PhD, Yi Murphey, PhD) S10OD026738-01 (PI Douglas Noll, PhD) R01 AG058724
                (PI Benjamin Hampstead, PhD) R35 AG072262 (PI Benjamin Hampstead, PhD) W81XWH2110743 (PI Benjamin
                Hampstead, PhD) R01 AG073235 (PI Nancy Chiaravalloti, University of Michigan Site PI Benjamin Hampstead,
                PhD) 1I01RX001534 (PI Benjamin Hampstead, PhD) IRX001381 (PI Benjamin Hampstead, PhD); University of New
                Mexico - P20 AG068077 (Gary Rosenberg, MD); University of Pennsylvania - State of PA project
                2019NF4100087335 (PI David Wolk, MD); Rooney Family Research Fund (PI David Wolk, MD); R01 AG055005 (PI
                David Wolk, MD); University of Pittsburgh - P50 AG005133 (PI Oscar Lopez MD); University of Southern
                California - P50 AG005142 (PI Helena Chui MD); University of Washington - P50 AG005136 (PI Thomas
                Grabowski MD); University of Wisconsin - P50 AG033514 (PI Sanjay Asthana MD FRCP); Vanderbilt University
                – P20 AG068082; Wake Forest - P30AG072947 (PI Suzanne Craft, PhD); Washington University, St. Louis -
                P01 AG03991 (PI John Morris MD); P01 AG026276 (PI John Morris MD); P20 MH071616 (PI Dan Marcus); P30
                AG066444 (PI John Morris MD); P30 NS098577 (PI Dan Marcus); R01 AG021910 (PI Randy Buckner); R01
                AG043434 (PI Catherine Roe); R01 EB009352 (PI Dan Marcus); UL1 TR000448 (PI Brad Evanoff); U24 RR021382
                (PI Bruce Rosen); Avid Radiopharmaceuticals / Eli Lilly; Yale - P50 AG047270 (PI Stephen Strittmatter MD
                PhD); R01AG052560 (MPI: Christopher van Dyck, MD; Richard Carson, PhD); R01AG062276 (PI: Christopher van
                Dyck, MD); 1Florida - P30AG066506-03 (PI Glenn Smith, PhD); P50 AG047266 (PI Todd Golde MD PhD)
              </i>
            </p>
          </TextBoxGray>

          <strong>
            If using data from the Alzheimer’s Disease Genetic Consortium (ADGC) the following statement acknowledging
            the ADGC grant is required in presentations and the acknowledgments section of manuscripts:
          </strong>
          <TextBoxGray>
            <p className="uk-text-small">
              <i>
                The Alzheimer&apos;s Disease Genetics Consortium (ADGC) is funded by a grant from the National Institute
                on Aging (PI, Gerard D. Schellenberg; UO1AG032984)
              </i>
            </p>
          </TextBoxGray>
        </SectionDefault>

        <SectionDefault subheading="Describing NACC data">
          <ul className="uk-list uk-list-bullet uk-margin-right">
            <li>
              Please be sure to review the{" "}
              <Link to="/data-collection/forms-documentation/uds-3">data-collection forms and documentation</Link>,
              especially the Coding Guidebooks and Data Element Dictionaries, for help in describing the NACC data in
              your manuscript. You may want to consult this{" "}
              <Link to="/publish-project/nacc-references">list of references</Link> cited in the coding guidebooks for
              the UDS and the FTLD and LBD Modules. These are the sources for the operational definitions and diagnostic
              criteria used in data collection.
            </li>
            <li>
              The papers by{" "}
              <a
                href="https://files.alz.washington.edu/documentation/weintraub-2009.pdf"
                title="Weintraub 2009"
                target="_blank"
                rel="noopener noreferrer"
              >
                Weintraub (2009)
              </a>
              ,{" "}
              <a
                href="https://files.alz.washington.edu/documentation/weintraub-2018-v3.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Weintraub (2018)
              </a>
              ,{" "}
              <a
                href="https://files.alz.washington.edu/documentation/beekly-2004.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Beekly (2004)
              </a>
              ,{" "}
              <a
                href="https://files.alz.washington.edu/documentation/beekly-2007.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Beekly (2007)
              </a>
              ,{" "}
              <a
                href="https://files.alz.washington.edu/documentation/besser-2018.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Besser (2018, on the Neuropathology Data Set)
              </a>
              ,{" "}
              <a
                href="https://files.alz.washington.edu/documentation/besser-2018-udsv3.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Besser (2018, on Version 3 of the UDS)
              </a>
              , and{" "}
              <a
                href="https://files.alz.washington.edu/documentation/morris-2006.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Morris (2006)
              </a>{" "}
              describe NACC data in detail and will also be helpful.
            </li>
            <li>
              NACC recommends that you include the phrase, “This analysis used data from X ADRCs,” consulting the{" "}
              <strong>NACCADC</strong> variable to determine how many ADRCs are represented in your data set. (The
              NACCADC number varies from one data set to another because the number of ADRCs has changed over time.)
            </li>
            <li>
              NACC recommends that you describe the specific span of time that is represented by your data. This can be
              accomplished by using the start date of the UDS (September 2005) and the data freeze on which your
              analysis was based (e.g., “… for UDS visits conducted between September 2005 and May 2012”). Please note
              that any given data freeze contains data from the previous month and before, such that the June freeze
              contains data from May and before). Please refer to the email documentation that you received with your
              data set or contact a NACC consultant to confirm the data freeze used for your data set. If your data set
              includes MDS subjects, please be aware that these data were abstracted from medical records starting in
              1984.
            </li>
            <li>
              If your research includes the CDR, note that Washington University requires that your manuscript&apos;s
              initial reference to the CDR&reg; use the following language and registered trademark symbol:
              &quot;CDR&#174; Dementia Staging Instrument. &quot;If your journal does not allow symbols, please include
              the full name of the instrument in your initial reference.
            </li>
            <li>
              Addition&quot;lly, if you are using the FTLD Module and/or the behavior, comportment, personality and
              language items collected on UDS Form B4, note that Washington University requires that your manuscript’s
              initial reference to the CDR&reg; and NACC’s additional FTLD Module components use the following language
              and registered trademark symbol: &quot;CDR® Dementia Staging Instrument plus NACC FTLD Behavior &amp;
              Language Domains.&quot; After that initial use, &quot;CDR&#174; plus NACC FTLD&quot; is acceptable. If
              your journal does not allow symbols, please include the full name of the instrument in your initial
              reference, and &quot;CDR&reg; plus NACC FTLD&quot; in the remainder of the manuscript.
            </li>
          </ul>
        </SectionDefault>

        <SectionDefault subheading="Using appropriate methods based on the nature of NACC data">
          <ul className="uk-list uk-list-bullet uk-margin-right">
            <li>
              Please note that the NACC data are not appropriate for studying the incidence or prevalence of
              MCI/dementia at the population level (city, county, state, etc.) because of the varying sampling
              strategies at each Center.
            </li>
            <li>
              If conducting survival analysis using NACC data, beware of common violations of model assumptions. For
              example, if a subject has become too impaired to continue follow-up, the reason for censoring may be
              closely related to the outcome. Please contact a NACC consultant with any questions about time-to-event
              analyses.
            </li>
            <li>
              NACC data are not ideally suited to study risk factors for dementia because of varying methods of subject
              recruitment across Centers and because of largely incomplete exposure histories.
            </li>
            <li>
              NACC consultants are happy to speak further with researchers about appropriate methodology. Please contact
              us at consnacc@uw.edu.
            </li>
          </ul>
        </SectionDefault>

        <SectionDefault subheading="Ensuring proper submission to PubMed Central (PMC)">
          <ul className="uk-list uk-margin-right">
            <li>
              In order to comply with the{" "}
              <a
                href="https://publicaccess.nih.gov"
                title="NIH Public Access Policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                NIH Public Access Policy
              </a>
              , all authors using NACC data must ensure the proper submission of their published work to PubMed Central
              (PMC).
            </li>
            <li>
              For more information on this process, please see{" "}
              <Link to="/publish-project/pmc-resources" title="Resources for Navigating PMC Submission">
                Resources for Navigating PMC Submission
              </Link>
              .
            </li>
          </ul>
        </SectionDefault>
      </GlobalLayout>
    );
  }
}

export default AuthorChecklist;
